import { createContext, useContext, useMemo, useState, useCallback, type ReactNode } from 'react';
import type { AccountInfo } from '@azure/msal-browser';

type MockAuthContextValue = {
  isAuthenticated: boolean;
  account: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  getAccessToken: (scopes: string[]) => Promise<string | null>;
  getGraphToken: () => Promise<string | null>;
  getArmToken: () => Promise<string | null>;
  getFoundryToken: () => Promise<string | null>;
  getStorageToken: () => Promise<string | null>;
  grantFoundryAccess: () => Promise<string | null>;
  grantGraphAccess: () => Promise<string | null>;
  grantStorageAccess: () => Promise<string | null>;
  getOttoToken: () => Promise<string | null>;
  clearTokenCache: () => Promise<void>;
};

// MockAuth identity. When pointed at the real Otto CP host (signaled by
// VITE_OTTO_CP_HOST=true), uses real GUIDs so BPM CP's RBAC check on
// `automationProjects/{p}/principals/{guid}` (which 400s on non-GUID values)
// accepts them; the localAccountId matches the dev OID in the JWT minted by
// tools/devtools/be-otto-cp-host (DevAadAuth.SignJwt) so the user has the
// Contributor role auto-seeded. When pointed at the in-tree mock CP (default),
// uses the legacy mock-shaped IDs that the mock CP's fixtures expect to match
// — using GUIDs there breaks Full-Stack E2E because mock CP returns
// `tenantId = "local-tenant"` and the portal filters projects by selected tenant.
const isOttoCpHost =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_OTTO_CP_HOST === 'true';
const MOCK_LOCAL_ACCOUNT_ID = isOttoCpHost ? '00000000-0000-0000-0000-000000000001' : 'mock-local-account-id';
const MOCK_TENANT_ID = isOttoCpHost ? '72f988bf-86f1-41af-91ab-2d7cd011db47' : 'mock-tenant-id';

const mockAccount: AccountInfo = {
  homeAccountId: isOttoCpHost ? `${MOCK_LOCAL_ACCOUNT_ID}.${MOCK_TENANT_ID}` : 'mock-home-account-id',
  localAccountId: MOCK_LOCAL_ACCOUNT_ID,
  environment: 'login.microsoftonline.com',
  tenantId: MOCK_TENANT_ID,
  username: 'testuser@example.com',
  name: 'Test User',
};

// Fetch a real signed JWT from Otto CP host's /__otto-cp-host__/sign-jwt endpoint.
// In dev/E2E with the Otto CP host launcher this produces a real JWT signed by
// the dev cert that Otto CP host validates via JwtEngine — exercising the
// production auth code path. Falls back to the legacy mock string when the
// launcher isn't running (e.g. Full-Stack E2E uses a mock CP at the same port
// that doesn't expose /__otto-cp-host__/sign-jwt; without a short timeout the
// fetch hangs ~30s waiting for the missing route and blocks portal render).
const fetchRealJwt = async (cache: { token?: string; expiresAt?: number }): Promise<string | null> => {
  if (cache.token && cache.expiresAt && cache.expiresAt > Date.now() + 5 * 60_000) {
    return cache.token;
  }
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 2_000);
  try {
    const cpUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ARM_BASE_URL) || 'http://localhost:4400';
    const response = await fetch(`${cpUrl}/__otto-cp-host__/sign-jwt`, { signal: controller.signal });
    if (!response.ok) {
      console.warn(`[MockAuth] /__otto-cp-host__/sign-jwt returned ${response.status}; falling back to placeholder`);
      return 'mock-otto-token';
    }
    const data = await response.json();
    cache.token = data.access_token;
    // JWT exp is 1 hour by default in DevAadAuth.SignJwt; cache for slightly less.
    cache.expiresAt = Date.now() + 55 * 60_000;
    return data.access_token;
  } catch (err) {
    console.warn(`[MockAuth] failed to fetch JWT from Otto CP host: ${err}; falling back to placeholder`);
    return 'mock-otto-token';
  } finally {
    clearTimeout(timeoutId);
  }
};

const MockAuthContext = createContext<MockAuthContextValue | null>(null);

// Module-scoped cache for the real JWT, shared across all hook callers.
// 55-minute lifetime (JWT exp is 1 hour, leeway 5 min).
const ottoTokenCache: { token?: string; expiresAt?: number } = {};

type MockAuthProviderProps = {
  children: ReactNode;
};

export const MockAuthProvider = ({ children }: MockAuthProviderProps) => {
  // Start unauthenticated so the landing page is visible in mock/dev mode.
  // E2E tests pre-set localStorage to skip the landing page.
  // Clicking "Sign in" calls login() which persists to localStorage.
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return localStorage.getItem('otto:mock-authenticated') === 'true';
    } catch {
      return false;
    }
  });

  const login = useCallback(async () => {
    try { localStorage.setItem('otto:mock-authenticated', 'true'); } catch { /* noop */ }
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    try { localStorage.removeItem('otto:mock-authenticated'); } catch { /* noop */ }
    setIsAuthenticated(false);
  }, []);

  const value: MockAuthContextValue = useMemo(() => ({
    isAuthenticated,
    account: isAuthenticated ? mockAccount : null,
    login,
    logout,
    isLoading: false,
    getAccessToken: async () => isAuthenticated ? 'mock-access-token' : null,
    getGraphToken: async () => isAuthenticated ? 'mock-graph-token' : null,
    getArmToken: async () => isAuthenticated ? 'mock-arm-token' : null,
    // Otto token: when running against the Otto CP host (VITE_OTTO_CP_HOST=true)
    // fetch a real JWT signed by the dev cert that CP validates via JwtEngine.
    // Otherwise return the legacy placeholder — the in-tree mock CP doesn't
    // validate Otto tokens and probing /__otto-cp-host__/sign-jwt on it just
    // adds a wasted round-trip plus 30s timeout in webkit if the route 404s.
    getOttoToken: async () => {
      if (!isAuthenticated) return null;
      if (!isOttoCpHost) return 'mock-otto-token';
      return await fetchRealJwt(ottoTokenCache);
    },
    getFoundryToken: async () => isAuthenticated ? 'mock-foundry-token' : null,
    getStorageToken: async () => isAuthenticated ? 'mock-storage-token' : null,
    grantFoundryAccess: async () => isAuthenticated ? 'mock-foundry-token' : null,
    grantGraphAccess: async () => isAuthenticated ? 'mock-graph-token' : null,
    grantStorageAccess: async () => isAuthenticated ? 'mock-storage-token' : null,
    clearTokenCache: async () => {
      delete ottoTokenCache.token;
      delete ottoTokenCache.expiresAt;
    },
  }), [isAuthenticated, login, logout]);

  return <MockAuthContext.Provider value={value}>{children}</MockAuthContext.Provider>;
};

export const useMockAuth = (): MockAuthContextValue => {
  const context = useContext(MockAuthContext);
  if (!context) {
    throw new Error('useMockAuth must be used within a MockAuthProvider');
  }
  return context;
};

// Re-export template components that are always rendered in mock mode
export const MockAuthenticatedTemplate = ({ children }: { children: ReactNode }) => <>{children}</>;
export const MockUnauthenticatedTemplate = ({ children }: { children: ReactNode }) => <>{children}</>;
